import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { CharacterRig, CharacterSpec, JointChain } from './CharacterBuilder';
import { getNumberTexture } from './textures';

// Bake costume and facial details into material batches; only joints stay separate.
type Part = { geometry: THREE.BufferGeometry; at?: [number, number, number]; scale?: [number, number, number]; turn?: [number, number, number] };
// Tiny facial details need fewer rings; keep the silhouette budget on the skull.
const sphere = (r: number) => new THREE.SphereGeometry(r, r >= 0.2 ? 16 : r < 0.09 ? 8 : 12, r >= 0.2 ? 10 : r < 0.09 ? 6 : 8);
const box = (x: number, y: number, z: number) => new THREE.BoxGeometry(x, y, z);
const material = (color: string, roughness = 0.72) => new THREE.MeshStandardMaterial({ color, roughness });

// Profile rings describe muscle and cloth rather than spending poles on capsules.
function limb(profile: Array<[number, number]>, depth = 1): THREE.BufferGeometry {
  return new THREE.LatheGeometry(profile.map(([radius, y]) => new THREE.Vector2(radius, y)).reverse(), 8).scale(1, 1, depth);
}

function shirtShell(width: number, height: number): THREE.BufferGeometry {
  // Twelve vertices per ring: curved chest/waist, sloping shoulders and a neck opening.
  const rings = [[-0.5, 0.88, 0.84], [-0.43, 0.94, 0.9], [-0.08, 0.88, 0.96],
    [0.22, 1, 1.1], [0.39, 1.04, 1], [0.5, 0.7, 0.76]];
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  const segments = 12;
  rings.forEach(([y, breadth, depth], ring) => {
    for (let i = 0; i <= segments; i++) {
      const angle = i / segments * Math.PI * 2;
      const x = Math.cos(angle), z = Math.sin(angle);
      positions.push(Math.sign(x) * Math.pow(Math.abs(x), 0.64) * width * breadth / 2,
        y * height, Math.sign(z) * Math.pow(Math.abs(z), 0.64) * 0.17 * depth);
      uv.push(i / segments, y + 0.5);
      if (ring && i < segments) {
        const a = (ring - 1) * (segments + 1) + i, b = a + segments + 1;
        indices.push(a, b, a + 1, a + 1, b, b + 1);
      }
    }
  });
  for (let i = 1; i < segments - 1; i++) {
    indices.push(0, i, i + 1);
    const last = (rings.length - 1) * (segments + 1);
    indices.push(last, last + i + 1, last + i);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function roundedBox(x: number, y: number, z: number, bevel = 0.035): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-x / 2 + bevel, -y / 2);
  shape.lineTo(x / 2 - bevel, -y / 2);
  shape.quadraticCurveTo(x / 2, -y / 2, x / 2, -y / 2 + bevel);
  shape.lineTo(x / 2, y / 2 - bevel);
  shape.quadraticCurveTo(x / 2, y / 2, x / 2 - bevel, y / 2);
  shape.lineTo(-x / 2 + bevel, y / 2);
  shape.quadraticCurveTo(-x / 2, y / 2, -x / 2, y / 2 - bevel);
  shape.lineTo(-x / 2, -y / 2 + bevel);
  shape.quadraticCurveTo(-x / 2, -y / 2, -x / 2 + bevel, -y / 2);
  return new THREE.ExtrudeGeometry(shape, { depth: z - bevel * 2, bevelEnabled: true, bevelSize: bevel, bevelThickness: bevel, bevelSegments: 1, steps: 1, curveSegments: 2 }).translate(0, 0, -z / 2 + bevel);
}

// A low-poly last: high heel, sloping instep and broad, lifted toe instead of an ellipsoid.
// Rings share vertices so the leather has continuous smooth highlights.
function bootLast(): THREE.BufferGeometry {
  const rings = [
    [-0.108, 0.045, 0.038, -0.305],
    [-0.07, 0.077, 0.069, -0.293],
    [0.015, 0.084, 0.062, -0.3],
    [0.12, 0.094, 0.044, -0.318],
    [0.205, 0.074, 0.031, -0.322],
    [0.236, 0.03, 0.018, -0.321],
  ];
  const positions: number[] = [];
  const uv: number[] = [];
  const indices: number[] = [];
  const segments = 10;
  for (let ring = 0; ring < rings.length; ring++) {
    const [z, width, height, y] = rings[ring];
    for (let i = 0; i < segments; i++) {
      const angle = i / segments * Math.PI * 2;
      positions.push(Math.cos(angle) * width, y + Math.sin(angle) * height, z);
      uv.push(i / segments, ring / (rings.length - 1));
      if (ring > 0) {
        const a = (ring - 1) * segments + i;
        const b = (ring - 1) * segments + (i + 1) % segments;
        const c = ring * segments + i;
        const d = ring * segments + (i + 1) % segments;
        indices.push(a, b, c, b, d, c);
      }
    }
  }
  for (let i = 1; i < segments - 1; i++) {
    indices.push(0, i + 1, i);
    const last = (rings.length - 1) * segments;
    indices.push(last, last + i, last + i + 1);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function bootSole(): THREE.BufferGeometry {
  const outline = new THREE.Shape();
  outline.moveTo(-0.052, -0.116);
  outline.lineTo(0.052, -0.116);
  outline.lineTo(0.082, -0.075);
  outline.lineTo(0.091, 0.025);
  outline.lineTo(0.101, 0.125);
  outline.lineTo(0.077, 0.218);
  outline.lineTo(0.032, 0.246);
  outline.lineTo(-0.032, 0.246);
  outline.lineTo(-0.077, 0.218);
  outline.lineTo(-0.101, 0.125);
  outline.lineTo(-0.091, 0.025);
  outline.lineTo(-0.082, -0.075);
  outline.closePath();
  return new THREE.ExtrudeGeometry(outline, { depth: 0.022, bevelEnabled: false, steps: 1 }).rotateX(Math.PI / 2);
}

function headSculpt(): THREE.BufferGeometry {
  // Continuous cheekbones, jaw and chin replace the overlapping cheek/jaw solids.
  const rings = [
    [-0.195, 0.09, 0.125, -0.02], [-0.165, 0.15, 0.205, -0.09],
    [-0.115, 0.19, 0.25, -0.15], [-0.05, 0.225, 0.262, -0.2],
    [0.025, 0.252, 0.245, -0.238], [0.105, 0.267, 0.232, -0.252],
    [0.185, 0.25, 0.214, -0.233], [0.255, 0.205, 0.163, -0.19],
    [0.31, 0.118, 0.08, -0.11], [0.335, 0.025, 0.005, -0.035],
  ];
  const segments = 16;
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  rings.forEach(([y, width, front, back], ring) => {
    for (let i = 0; i < segments; i++) {
      const angle = i / segments * Math.PI * 2;
      const x = Math.cos(angle), z = Math.sin(angle);
      positions.push(Math.sign(x) * Math.pow(Math.abs(x), 0.82) * width,
        y, -0.015 + Math.pow(Math.abs(z), z > 0 ? 0.48 : 0.9) * (z > 0 ? front + 0.015 : back + 0.015));
      uv.push(i / segments, ring / (rings.length - 1));
      if (ring) {
        const a = (ring - 1) * segments + i, b = ring * segments + i;
        const next = (i + 1) % segments;
        indices.push(a, b, (ring - 1) * segments + next, (ring - 1) * segments + next, b, ring * segments + next);
      }
    }
  });
  for (let i = 1; i < segments - 1; i++) {
    indices.push(0, i, i + 1);
    const last = (rings.length - 1) * segments;
    indices.push(last, last + i + 1, last + i);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function expression(parent: THREE.Group, name: string, pivot: [number, number, number]): THREE.Group {
  const group = new THREE.Group();
  group.name = name;
  group.position.set(...pivot);
  parent.add(group);
  return group;
}

function local(parts: Part[], group: THREE.Group): Part[] {
  for (const part of parts) {
    const at = part.at ?? [0, 0, 0];
    part.at = [at[0] - group.position.x, at[1] - group.position.y, at[2] - group.position.z];
  }
  return parts;
}

function batch(parent: THREE.Object3D, mat: THREE.Material, parts: Part[], name: string): THREE.Mesh {
  const transform = new THREE.Object3D();
  const geometries = parts.map(part => {
    transform.position.set(...(part.at ?? [0, 0, 0]));
    transform.scale.set(...(part.scale ?? [1, 1, 1]));
    transform.rotation.set(...(part.turn ?? [0, 0, 0]));
    transform.updateMatrix();
    const geometry = part.geometry.clone().applyMatrix4(transform.matrix);
    // Keep shared surface vertices; only extruded parts need a sequential index.
    if (!geometry.index) geometry.setIndex(Array.from({ length: geometry.getAttribute('position').count }, (_, i) => i));
    if (mat instanceof THREE.MeshStandardMaterial && mat.vertexColors) {
      const positions = geometry.getAttribute('position');
      const colors = new Float32Array(positions.count * 3);
      for (let i = 0; i < positions.count; i++) {
        const x = Math.abs(positions.getX(i));
        const z = positions.getZ(i);
        const shade = (x > 0.21 ? 0.74 : 0.96) * (z > 0.12 && x < 0.13 ? 1.12 : 1);
        colors[i * 3] = colors[i * 3 + 1] = colors[i * 3 + 2] = shade;
      }
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }
    return geometry;
  });
  const geometry = mergeGeometries(geometries);
  for (let i = 0; i < geometries.length; i++) {
    geometries[i].dispose();
    parts[i].geometry.dispose();
  }
  const result = new THREE.Mesh(geometry, mat);
  result.name = name;
  result.castShadow = true;
  parent.add(result);
  return result;
}

export function buildCharacter(spec: CharacterSpec): CharacterRig {
  const root = new THREE.Group();
  root.name = `footballer-${spec.id}`;
  const hips = new THREE.Group();
  hips.position.y = 0.72;
  root.add(hips);
  const torso = new THREE.Group();
  torso.position.y = 0.03;
  hips.add(torso);
  const skin = material(spec.skin);
  const jersey = new THREE.MeshStandardMaterial({ color: spec.jersey, roughness: 0.84, vertexColors: true });
  const shorts = material(spec.shorts, 0.88);
  const trim = material(spec.numberColor, 0.8);
  const dark = material('#202333');
  const white = material('#fff9e7', 0.5);
  const hair = material(spec.hair, 0.92);
  const socks = material(spec.socks, 0.88);
  const leather = material(spec.boots, 0.48);
  const glove = material(spec.gloves, 0.65);
  const breadth = spec.isGoalkeeper ? 1.12 : 1;
  const torsoShell = shirtShell(0.51, 0.5);

  batch(torso, jersey, [
    { geometry: torsoShell, at: [0, 0.29, 0] },
  ], 'tailored-jersey');
  batch(torso, trim, [
    { geometry: new THREE.TorusGeometry(0.108, 0.023, 5, 12), at: [0, 0.548, 0], turn: [Math.PI / 2, 0, 0] },
    { geometry: box(0.14, 0.028, 0.012), at: [0.13, 0.38, 0.185], turn: [0, 0, -0.16] },
    { geometry: box(0.047, 0.08, 0.016), at: [-0.13, 0.37, 0.186] },
    { geometry: box(0.45, 0.025, 0.28), at: [0, 0.065, 0] },
    { geometry: box(0.14, 0.025, 0.012), at: [0.065, 0.29, 0.188], turn: [0, 0, 0.26] },
    { geometry: box(0.14, 0.025, 0.012), at: [-0.065, 0.29, 0.188], turn: [0, 0, -0.26] },
  ], 'collar-crest-and-hem');
  batch(hips, shorts, [
    { geometry: roundedBox(0.42, 0.19, 0.31, 0.025), at: [0, -0.08, 0] },
  ], 'shorts-waist');
  batch(hips, trim, [
    { geometry: box(0.026, 0.18, 0.12), at: [0.21, -0.09, 0] },
    { geometry: box(0.026, 0.18, 0.12), at: [-0.21, -0.09, 0] },
    { geometry: box(0.016, 0.075, 0.012), at: [-0.023, -0.02, 0.17], turn: [0, 0, -0.24] },
    { geometry: box(0.016, 0.06, 0.012), at: [0.023, -0.013, 0.17], turn: [0, 0, 0.24] },
  ], 'shorts-piping-and-drawstrings');

  const numberPlate = new THREE.Mesh(new THREE.PlaneGeometry(0.29, 0.29), new THREE.MeshStandardMaterial({
    map: getNumberTexture(spec.number, spec.numberColor, spec.id), transparent: true, roughness: 0.8,
    polygonOffset: true, polygonOffsetFactor: -1,
  }));
  numberPlate.position.set(0, 0.29, -0.19);
  numberPlate.rotation.y = Math.PI;
  torso.add(numberPlate);

  const arm = (side: -1 | 1): JointChain => {
    const upper = new THREE.Group();
    upper.name = side > 0 ? 'shoulder-left' : 'shoulder-right';
    upper.position.set(side * 0.28 * breadth, 0.46, 0);
    torso.add(upper);
    batch(upper, jersey, [{ geometry: limb([[0, 0.02], [0.09, 0.02], [0.117, -0.025], [0.108, -0.12], [0.09, -0.145], [0, -0.145]], 0.94) }], 'jersey-sleeve');
    batch(upper, skin, [{ geometry: limb([[0, -0.06], [0.063, -0.075], [0.089, -0.14], [0.076, -0.21], [0.052, -0.27], [0, -0.28]], 0.88) }], 'upper-arm');
    const lower = new THREE.Group();
    lower.position.y = -0.25;
    upper.add(lower);
    batch(lower, skin, [{ geometry: limb([[0, 0.01], [0.056, 0], [0.077, -0.055], [0.068, -0.12], [0.046, -0.19], [0, -0.2]], 0.9) }], 'forearm');
    batch(lower, jersey, [{ geometry: new THREE.CylinderGeometry(0.077, 0.077, 0.055, 10, 1, true), at: [0, -0.17, 0] }], 'team-wristband');
    const hand = new THREE.Group();
    hand.position.y = -0.23;
    hand.scale.set(1, 0.92, spec.isGoalkeeper ? 0.82 : 0.95);
    lower.add(hand);
    const size = spec.isGoalkeeper ? 1.22 : 1.08;
    const fingers: Part[] = spec.isGoalkeeper
      ? Array.from({ length: 4 }, (_, i) => ({
        geometry: limb([[0, 0.01], [0.023, 0], [0.023, -0.07 - (i === 1 || i === 2 ? 0.025 : 0)], [0, -0.095 - (i === 1 || i === 2 ? 0.025 : 0)]], 0.85),
        at: [(i - 1.5) * 0.045 * size, -0.082, 0.008], turn: [0, 0, (i - 1.5) * 0.075],
      }))
      : Array.from({ length: 4 }, (_, i) => ({
        geometry: box(0.033 * size, 0.048 * size, 0.045 * size),
        at: [(i - 1.5) * 0.039 * size, -0.058, 0.076 * size], turn: [0, 0, side * 0.08],
      }));
    batch(hand, glove, [
      { geometry: roundedBox(0.17 * size, 0.155 * size, 0.135 * size, 0.027 * size), at: [0, -0.022, 0.008] },
      ...fingers,
      { geometry: sphere(0.041 * size), at: [-side * 0.077 * size, -0.015, 0.035] },
      { geometry: new THREE.CylinderGeometry(0.069 * size, 0.069 * size, 0.06, 10), at: [0, 0.06, 0] },
    ], 'glove-fist-and-thumb');
    return { upper, lower, tip: hand };
  };
  const armL = arm(1);
  const armR = arm(-1);

  const leg = (side: -1 | 1): JointChain => {
    const upper = new THREE.Group();
    upper.position.x = side * 0.125;
    hips.add(upper);
    batch(upper, shorts, [{ geometry: limb([[0.115, -0.005], [0.126, -0.07], [0.119, -0.16], [0.103, -0.282]], 0.93) }], 'shorts-leg');
    const lower = new THREE.Group();
    lower.position.y = -0.31;
    upper.add(lower);
    batch(lower, socks, [
      { geometry: limb([[0, 0.035], [0.068, 0.025], [0.084, -0.075], [0.073, -0.16], [0.048, -0.255], [0, -0.275]], 0.92) },
    ], 'shin-and-sock');
    batch(lower, trim, [{ geometry: new THREE.CylinderGeometry(0.079, 0.079, 0.035, 10, 1, true), at: [0, -0.04, 0] }], 'sock-cuff');
    const foot = new THREE.Group();
    foot.position.y = -0.25;
    const bootScale = 1.04;
    foot.scale.set(bootScale, 1, bootScale);
    lower.add(foot);
    batch(foot, leather, [
      { geometry: bootLast() },
    ], 'football-boot');
    batch(foot, trim, [
      { geometry: bootSole(), at: [0, -0.358, 0] },
      { geometry: box(0.095, 0.009, 0.016), at: [0, -0.25, 0.045], turn: [-0.16, 0, 0.1] },
      { geometry: box(0.1, 0.009, 0.016), at: [0, -0.267, 0.085], turn: [-0.16, 0, -0.1] },
      { geometry: box(0.013, 0.045, 0.09), at: [0.086 * side, -0.315, 0.08], turn: [0.3, 0, 0] },
    ], 'sole-and-laces');
    // Rotate and enlarge the boot around the ankle, rather than the knee.
    for (const part of foot.children) part.position.y += 0.25;
    return { upper, lower, tip: foot };
  };
  const legL = leg(1);
  const legR = leg(-1);

  const head = new THREE.Group();
  head.position.y = 0.78;
  torso.add(head);
  batch(head, skin, [
    { geometry: headSculpt() },
    { geometry: sphere(0.067), at: [0, 0.02, 0.245], scale: [0.85, 0.8, 1.25] },
    ...([-1, 1] as const).map(side => ({ geometry: sphere(0.059), at: [side * 0.265, 0.045, 0] as [number, number, number], scale: [0.65, 1, 0.7] as [number, number, number] })),
    { geometry: new THREE.CylinderGeometry(0.09, 0.11, 0.18, 10), at: [0, -0.2, 0] },
  ], 'sculpted-head-jaw-ears-and-nose');
  const eyes: Part[] = [];
  const pupils: Part[] = [];
  const brows: Part[] = [];
  for (const side of [-1, 1] as const) {
    eyes.push({ geometry: sphere(0.061), at: [side * 0.103, 0.097, 0.218], scale: [1.05, 0.8, 0.45] });
    pupils.push({ geometry: sphere(0.026), at: [side * 0.103, 0.097, 0.244], scale: [0.8, 1, 0.5] });
    brows.push({ geometry: roundedBox(0.112, 0.032, 0.028, 0.008), at: [side * 0.105, 0.172 + (side === 1 && spec.eyes === 'happy' ? 0.023 : 0), 0.23], turn: [0, 0, side * (spec.eyes === 'happy' ? -0.23 : spec.eyes === 'fierce' ? 0.32 : 0.14)] });
  }
  const eyeGroup = expression(head, 'expression-eyes', [0, 0.1, 0.24]);
  batch(eyeGroup, white, local(eyes, eyeGroup), 'eye-whites');
  batch(eyeGroup, dark, local(pupils, eyeGroup), 'pupils');
  const browGroup = expression(head, 'expression-brows', [0, 0.17, 0.25]);
  batch(browGroup, hair, local(brows, browGroup), 'expressive-brows');
  const mouth = expression(head, 'expression-mouth', [0, -0.075, 0.29]);
  const grin = spec.eyes === 'happy';
  batch(mouth, dark, [{ geometry: sphere(grin ? 0.1 : 0.087), at: [0, 0, -0.026], scale: [1, grin ? 0.44 : 0.29, 0.24] }], 'mouth-opening');
  batch(mouth, white, [{ geometry: roundedBox(grin ? 0.15 : 0.125, grin ? 0.036 : 0.022, 0.012, 0.005), at: [0, grin ? 0.015 : 0.007, -0.003], turn: [0, 0, spec.eyes === 'focused' ? -0.12 : 0] }], 'comic-teeth');

  const locks: Part[] = [];
  if (spec.hairStyle !== 'bald') {
    const cap = new THREE.SphereGeometry(0.282, 12, 7, 0, Math.PI * 2, 0, Math.PI / 2);
    const positions = cap.getAttribute('position');
    for (let i = 0; i < positions.count; i++) positions.setY(i, positions.getY(i) * 0.8 + Math.max(0, positions.getZ(i)) * 0.48);
    cap.computeVertexNormals();
    locks.push({ geometry: cap, at: [0, 0.12, -0.023], scale: [1.04, 1, 1] });
    if (spec.hairStyle === 'spiky') {
      for (let i = 0; i < 5; i++) locks.push({ geometry: new THREE.ConeGeometry(0.083, 0.2, 5, 1, true), at: [(i - 2) * 0.081, 0.38 - Math.abs(i - 2) * 0.021, -0.055], scale: [1, 1, 0.62], turn: [-0.62, 0, -(i - 2) * 0.3] });
    } else if (spec.hairStyle === 'mop') {
      for (let i = 0; i < 3; i++) locks.push({
        geometry: limb([[0, 0.14], [0.025, 0.12], [0.065, 0.04], [0.058, -0.04], [0.025, -0.11], [0, -0.14]], 0.65),
        at: [(i - 1) * 0.105, 0.285 + i * 0.016, 0.162], turn: [0.35, 0, -0.95],
      });
    } else {
      locks.push({ geometry: new THREE.CapsuleGeometry(0.068, 0.26, 4, 8), at: [0, 0.005, -0.34], turn: [0.6, 0, 0] });
      batch(head, trim, [{ geometry: sphere(0.072), at: [0, 0.12, -0.27], scale: [1, 0.45, 1] }], 'ponytail-band');
    }
    const hairGroup = expression(head, 'secondary-hair', [0, 0.23, 0]);
    batch(hairGroup, hair, local(locks, hairGroup), `hair-${spec.hairStyle}`);
  } else {
    batch(head, hair, [{ geometry: box(0.14, 0.018, 0.025), at: [0, -0.105, 0.267] }], 'stubble');
  }

  return { root, hips, torso, head, armL, armR, legL, legR, numberPlate };
}
